import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, viewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from '@src/app/model/userdata';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { LoginService } from '@src/app/services/login/login.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-mfa',
  templateUrl: './mfa.component.html',
  styleUrls: ['./mfa.component.scss'],
  standalone: false,
})
export class MfaComponent implements OnInit, AfterViewInit, OnDestroy {
  public waitingForResponse: boolean = false; // used to show spinner
  public completed: boolean = false;
  public translateBase: string = 'login.mfa.';
  public error: boolean = false;
  public mfaForm: FormGroup;
  public user: User;
  public qrImage: string;
  private _componentDestroyed$: Subject<void> = new Subject();

  constructor(
    private _formBuilder: FormBuilder,
    private _apolloService: ApolloService,
    private _loginService: LoginService,
    private _router: Router,
    private _changeDetectorRef: ChangeDetectorRef) {
    this.mfaForm = this._formBuilder.group({
      mfaCode: ['', [Validators.required]],
    });
  }

  async ngOnInit(): Promise<void> {
    // obtain current user. if it contains a QR image for TOTP, then the user needs to (re)configure the MFA app.
    await this._apolloService.getCurrentUser$()
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe({
        next: (result: User) => {
          this.user = result;
          this.qrImage = this.user.totpQr;
          if (!!this.qrImage) {
            // change translation base for activation instead of just validation
            this.translateBase = 'login.mfa-activate.';
          }
        },
        error: () => {
          this.user = new User('An error occurred', '', null, []);
        },
      });
  }

  // focus on input on opening the page
  readonly totpCodeInput = viewChild<ElementRef>('totpCodeInput');

  public ngAfterViewInit(): void {
    this.totpCodeInput().nativeElement.focus();
    this._changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
  }

  async submit(): Promise<void> {
    if (this.mfaForm.valid) {
      this.error = false;
      this.waitingForResponse = true;
      // attempt MFA request
      await this._loginService.performMfa$(this.mfaForm.value.mfaCode).subscribe(
        (success: boolean) => {
          this.waitingForResponse = false;
          if (success) {
            this.completed = true;
            this._router.navigateByUrl('/landing');
          } else {
            this.error = true;
          }
        },
        () => {
          this.waitingForResponse = false;
          this.error = true;
        }
      );
    }
  }

  async logout(): Promise<void> {
    await this._loginService.logout$().toPromise();
    this._router.navigateByUrl('/login');
  }

  /**
   * Navigate back.
   */
  public back(): void {
    this._router.navigateByUrl(
      'login'
    );
  }
}
